import React, { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  TextInput,
  Checkbox,
  Group,
  Button,
  Text,
  SegmentedControl,
  ColorInput,
  Select,
} from '@mantine/core';
import { isBagelAdmin } from '../../../utils';
import { isBagelAiTab } from '../../../utils/viewUtils';
import { isBagelEmployee } from '../../../utils';
import { useCurrentUser } from "../../../hooks/useCurrentUser";

const TAB_COLOR_RESET = '#FFFFFF';

const TAB_COLOR_SWATCHES = [
  TAB_COLOR_RESET,
  '#FF8700',
  '#FA5252',
  '#E64980',
  '#BE4BDB',
  '#7950F2',
  '#5B60E3',
  '#4C6EF5',
  '#228BE6',
  '#15AABF',
  '#12B886',
  '#40C057',
  '#82C91E',
  '#FAB005',
  '#FD7E14',
  '#868E96',
];

const isResetColor = (value: string): boolean =>
  value?.toUpperCase() === TAB_COLOR_RESET;

const LEVEL_OPTIONS = [
  { value: 'ideas', label: 'Ideas' },
  { value: 'themes', label: 'Themes' },
];

interface SaveViewModalProps {
  opened: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  viewName: string;
  setViewName: (name: string) => void;
  isGlobal: boolean;
  setIsGlobal: (isGlobal: boolean) => void;
  isSubPageView: boolean;
  setIsSubPageView: (isSubPageView: boolean) => void;
  canEdit: boolean;
  view: any;
}

const SaveViewModal: React.FC<SaveViewModalProps> = ({
  opened,
  onClose,
  onSave,
  viewName,
  setViewName,
  isGlobal,
  setIsGlobal,
  isSubPageView,
  setIsSubPageView,
  canEdit,
  view
}) => {
  const currentUser = useCurrentUser();
  const isBagelUser = isBagelEmployee(currentUser);
  const defaultLevelFor = (v: any): string => (isBagelAiTab(v) ? 'themes' : 'ideas');

  const [color, setColor] = useState<string>(view?.color || '');
  const [level, setLevel] = useState<string>(view?.level || defaultLevelFor(view));

  const [viewTitle, setViewTitle] = useState(view?.tooltip || '');
  const [operator, setOperator] = useState(view?.operator || 'AND')

  // Initialize from current view (or defaults) when the modal opens
  useEffect(() => {
    if (!opened) return;
    setViewTitle(view?.tooltip || '');
    setOperator(view?.operator || 'AND');
    if (view) {
      setColor(view.color || '');
      setLevel(view.level || defaultLevelFor(view));
    }
  }, [opened, view]);

  const handleSave = async () => {
    try {
      await onSave({
        ...(view?._id ? { _id: view._id } : {}),
        name: viewName,
        tooltip: viewTitle,
        isGlobal: isGlobal,
        isSubPageView: isSubPageView,
        operator: isBagelUser ? operator : (view?.operator || 'AND'),
        color,
        level,
      });
    } catch (error) {
      console.error('Failed to save view:', error);
    }
  };

  const handleClose = (): void => {
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Save View"
      styles={{
        title: {
          fontWeight: 600
        },
        header: {
          fontWeight: 600
        }
      }}
      size="sm"
      centered
    >
      <Stack spacing="md">
        <TextInput
          label="View Name"
          value={viewName}
          onChange={(event) => setViewName(event.currentTarget.value)}
          placeholder="Enter view name"
          required
        />
        <TextInput
          label="View Tooltip"
          value={viewTitle}
          onChange={(event) => setViewTitle(event.currentTarget.value)}
          placeholder="Enter view tooltip (optional)"
        />
        {isBagelUser && (
          <div>
            <Text size="sm" fw={500} mb={6}>
              Match filters
            </Text>
            <SegmentedControl
              fullWidth
              data={[
                { label: 'All (AND)', value: 'AND' },
                { label: 'Any (OR)', value: 'OR' },
              ]}
              value={operator}
              onChange={(v) => setOperator(v as 'AND' | 'OR')}
              color="indigo"
            />
          </div>
        )}
        {isBagelUser && (
          <ColorInput
            label="Tab Color"
            placeholder="Pick a color (optional)"
            value={color}
            onChange={(value) => setColor(isResetColor(value) ? '' : value)}
            format="hex"
            swatches={TAB_COLOR_SWATCHES}
            swatchesPerRow={8}
            withinPortal
          />
        )}
        {isBagelUser && !isBagelAiTab(view) && (
          <Select
            label="Default Level"
            value={level}
            onChange={(value) => setLevel(value || 'ideas')}
            data={LEVEL_OPTIONS}
            withinPortal
          />
        )}
        {canEdit && (
          <Checkbox
            label="Visible to all"
            checked={isGlobal}
            onChange={(event) => setIsGlobal(event.currentTarget.checked)}
            color="indigo"
          />
        )}
        {canEdit && (
          <Checkbox
            label="Add to sub-pages"
            checked={isSubPageView}
            onChange={(event) => setIsSubPageView(event.currentTarget.checked)}
            color="indigo"
          />
        )}
        <Group position="right" spacing="md">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              handleSave();
              handleClose();
            }}
            disabled={!viewName?.trim()}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default SaveViewModal; 